import { MouseEvent, TouchEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { delay, fromEvent, mapTo, of, race, switchMap, tap } from 'rxjs';
import { longPressDurationMs, rowHeight } from './Table.config';
import { TableCell } from './TableCell';
import {
  StyledTableRow,
  TableRowCells,
  TableRowExpanderCell,
  TableRowExpanderIcon,
} from './TableRow.styled';
import { TableRowDetails } from './TableRowDetails';
import { TableRowProps } from './types';

export const TableRow = <TEntity extends unknown>(props: TableRowProps<TEntity>) => {
  const {
    columns,
    entity,
    expandable,
    expanded,
    highlighted = false,
    index,
    selectable = false,
    selected = false,
    style,
    onCanExpand,
    onDetailsRender,
    onDetailsToggle,
    onDoubleClick,
    onResize,
    onSelect,
  } = props;

  // Row element reference
  const ref = useRef<HTMLDivElement>(null);

  // Pre-render the expanded component if current row supports being expanded
  const detailsExpander = useMemo(() => {
    // Only render the expander if the row is expandable
    if (!expandable) {
      return null;
    }

    // Don't render the expander if the individual row is not expandable
    if (onCanExpand && !onCanExpand(entity, index)) {
      return <TableRowExpanderCell />;
    }

    /**
     * Handle clicking on the expand/collapse chevron
     *
     * @param {MouseEvent} event Mouse event object
     */
    const handleExpanderClick = (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      onDetailsToggle(entity, !expanded);
    };

    return (
      <TableRowExpanderCell>
        <TableRowExpanderIcon
          $expanded={expanded}
          name="chevron right"
          onClick={handleExpanderClick}
        />
      </TableRowExpanderCell>
    );
  }, [entity, expandable, expanded, index, onCanExpand, onDetailsToggle]);

  // Pre-render and memoize row details
  const detailsContent = useMemo(() => {
    // Ensure row is expandable and is currently expanded
    if (!expandable || !expanded || !onDetailsRender) {
      return null;
    }

    /**
     * Handle row details being expanded or collapsed
     */
    const handleDetailsResize = (detailsHeight?: number) => {
      // Ignore changes when the details component is unmounted due to being scrolled away or closed
      if (typeof detailsHeight === 'undefined') {
        return;
      }

      // Notify parent about a height change of the current row
      onResize(entity, index, rowHeight + detailsHeight);
    };

    return (
      <TableRowDetails onResize={handleDetailsResize}>{onDetailsRender(entity)}</TableRowDetails>
    );
  }, [entity, expandable, expanded, index, onDetailsRender, onResize]);

  /**
   * Handle clicking on the current row
   *
   * @param {MouseEvent} event Mouse event object
   */
  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.detail === 1) {
        // Handle single clicks
        selectable &&
          onSelect &&
          onSelect(index, !selected, event.shiftKey, event.ctrlKey || event.metaKey);
      } else if (event.detail === 2) {
        // Handle double clicks
        onDoubleClick && onDoubleClick(entity);
      }
    },
    [entity, index, selectable, selected, onDoubleClick, onSelect],
  );

  /**
   * Handle tapping or long-pressing on the current row
   *
   * @param {boolean} isLongPress TRUE if a long-press was performed, FALSE if a short tap was
   */
  const handleTapEvents = useCallback(
    (isLongPress: boolean) =>
      isLongPress
        ? onDoubleClick && onDoubleClick(entity)
        : onSelect && onSelect(index, !selected, false, true),
    [entity, index, selected, onDoubleClick, onSelect],
  );

  // Attach touchstart event listener to be able to mark it as active
  useEffect(() => {
    // Ensure the element is mounted before proceeding
    if (!ref.current) {
      return;
    }

    // Touch event emitters
    const touchStart$ = fromEvent<TouchEvent>(ref.current, 'touchstart', { passive: true });
    const touchEnd$ = fromEvent<TouchEvent>(ref.current, 'touchend', { passive: true });

    // Subscribe to touch start events and emit TRUE for long presses and FALSE for taps
    const touchEvent$ = touchStart$.pipe(
      switchMap(event =>
        race(
          of(event).pipe(delay(longPressDurationMs), mapTo(true)),
          touchEnd$.pipe(
            tap(event => event.preventDefault()),
            mapTo(false),
          ),
        ),
      ),
    );

    // Subscribe to long press and short press events
    const subscription = touchEvent$.subscribe(handleTapEvents);

    return () => subscription.unsubscribe();
  }, [handleTapEvents]);

  // Notify parent table about details being closed and height of the current
  // row changing back to its default
  useEffect(() => {
    if (expanded) {
      return;
    }

    onResize(entity, index, rowHeight);
  }, [entity, expanded, index, onResize]);

  return (
    <StyledTableRow ref={ref} role="row" style={style}>
      <TableRowCells
        $highlighted={highlighted}
        $selectable={selectable}
        $selected={selectable && selected}
        $static={false}
        onClick={handleClick}
      >
        {detailsExpander}
        {columns.map(column => (
          <TableCell column={column} entity={entity} key={column.id.toString()} />
        ))}
      </TableRowCells>

      {detailsContent}
    </StyledTableRow>
  );
};
